/**
 * FormField — label + input + optional hint/error.
 *
 * Extracts the repeated form field pattern from admin pages.
 * Follows Card.tsx pattern: forwardRef, clsx, className passthrough.
 */

import { forwardRef, type InputHTMLAttributes } from 'react'
import { clsx } from 'clsx'

export interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Field label text. */
  label: string
  /** Whether the field is required (shows * indicator). */
  fieldRequired?: boolean
  /** Hint text below the input. */
  hint?: string
  /** Error message (replaces hint when present). */
  error?: string
  /** Additional classes for the wrapper div. */
  wrapperClassName?: string
}

/** Reusable form field with consistent styling across admin and environment pages. */
export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  ({ label, fieldRequired, hint, error, wrapperClassName, className, id, ...props }, ref) => {
    return (
      <div className={clsx('space-y-1.5', wrapperClassName)}>
        <label htmlFor={id} className="block text-sm font-medium text-content-secondary">
          {label}
          {fieldRequired && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <input
          ref={ref}
          id={id}
          aria-required={fieldRequired || undefined}
          required={fieldRequired || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          className={clsx(
            'w-full px-3 py-2 border border-border rounded-lg transition-all',
            'bg-surface-input text-content',
            'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'placeholder:text-content-tertiary',
            error && 'border-error focus:border-error focus:ring-error/20',
            className,
          )}
          {...props}
        />
        {error ? (
          <p id={`${id}-error`} className="text-xs text-error" role="alert">
            {error}
          </p>
        ) : hint ? (
          <p id={`${id}-hint`} className="text-xs text-content-tertiary">
            {hint}
          </p>
        ) : null}
      </div>
    )
  },
)

FormField.displayName = 'FormField'
